// Shared contract for handing details to the quote wizard (/get-quote).
//
// Several pages offer a short "Get Your Free Estimate" form — the homepage hero and
// every service page — but the real form is the four-step wizard. Those short forms
// collect a few fields and hand them over through router state, so a visitor never
// retypes what they already entered. Keeping the shape, the service options and the
// mapping here means a new entry point cannot drift out of sync with the wizard.

// The service choices offered on step 3 of the wizard.
export const QUOTE_SERVICE_OPTIONS = [
  'Roof Cleaning',
  'Roof Repair',
  'Roof Replacement',
  'Commercial Roofing',
  'Emergency Service',
  'Roof Inspection',
  'Insurance Claims',
  'Gutter Cleaning',
  'Not Sure / Multiple',
] as const;

export type QuoteForm = {
  name: string; email: string; phone: string; address: string; city: string; service: string;
  roofAge: string; roofType: string; squareFootage: string; urgency: string; notes: string;
};

export const EMPTY_QUOTE_FORM: QuoteForm = {
  name: '', email: '', phone: '', address: '', city: '', service: '',
  roofAge: '', roofType: '', squareFootage: '', urgency: '', notes: '',
};

/** Any subset of the wizard's fields another page can fill in ahead of time. */
export type QuotePrefill = Partial<QuoteForm>;

// Service pages and the wizard name the same work differently ("Emergency Roofing"
// vs "Emergency Service"), so a service slug is translated rather than passed
// straight through — an unmatched string would leave step 3 with nothing selected.
const SERVICE_BY_SLUG: Record<string, string> = {
  'roof-cleaning': 'Roof Cleaning',
  'roof-repair': 'Roof Repair',
  'roof-replacement': 'Roof Replacement',
  'commercial-roofing': 'Commercial Roofing',
  'emergency-roofing': 'Emergency Service',
  'roof-inspection': 'Roof Inspection',
  'insurance-claims': 'Insurance Claims',
  'gutter-cleaning': 'Gutter Cleaning',
};

// Labels used by the homepage hero's service dropdown.
const SERVICE_BY_LABEL: Record<string, string> = {
  'Roof Replacement': 'Roof Replacement',
  'Insurance Claim Assistance': 'Insurance Claims',
  'Roof Repair': 'Roof Repair',
  'Free Roof Inspection': 'Roof Inspection',
  'Commercial Roofing': 'Commercial Roofing',
  'Emergency Service': 'Emergency Service',
  'Roof Cleaning': 'Roof Cleaning',
  'Gutter Cleaning': 'Gutter Cleaning',
};

/** Wizard service option for a service-page slug; '' when there is no equivalent. */
export const quoteServiceForSlug = (slug?: string): string => SERVICE_BY_SLUG[slug ?? ''] ?? '';

/** Wizard service option for a display label; '' when there is no equivalent. */
export const quoteServiceForLabel = (label?: string): string => SERVICE_BY_LABEL[label ?? ''] ?? '';

/** Navigate options carrying a prefill: navigate(ROUTES.QUOTE, quoteNavState({...})). */
export const quoteNavState = (prefill: QuotePrefill) => ({ state: { prefill } });

/**
 * Read a prefill back out of router state, keeping only non-empty strings so a
 * blank field on the sending page never wipes one already typed in the wizard.
 * Returns null when there is nothing to apply.
 */
export const readQuotePrefill = (state: unknown): QuotePrefill | null => {
  const prefill = (state as { prefill?: QuotePrefill } | null)?.prefill;
  if (!prefill) return null;
  const filled = Object.entries(prefill).filter(
    ([key, value]) => key in EMPTY_QUOTE_FORM && typeof value === 'string' && value.trim() !== ''
  );
  return filled.length ? (Object.fromEntries(filled) as QuotePrefill) : null;
};
